import React, { useEffect, useMemo, useState } from 'react';
import { Plus, Minus, Trash2 } from 'lucide-react';
import { api, formatApiError } from '../lib/api';
import { Modal } from '../lib/Modal';
import { displayCategoryEmoji } from '../lib/categoryEmoji';
import { sortTablesByNumber } from '../lib/tableSort';

function cartKey(item) {
    const extra = item.is_extra && item.source === 'client' ? ':extra' : '';
    return `${item.product_id}:${item.variant_id}${extra}`;
}

function normalizeCartItem(item) {
    const row = {
        product_id: String(item.product_id || ''),
        variant_id: String(item.variant_id || ''),
        product_name: String(item.product_name || 'Produs'),
        variant_name: String(item.variant_name || 'Porție'),
        price: Number(item.price) || 0,
        quantity: Math.max(1, Number(item.quantity) || 1),
        notes: item.notes ? String(item.notes) : '',
    };
    if (item.is_extra && item.source === 'client') {
        row.is_extra = true;
        row.source = 'client';
        if (item.added_at) row.added_at = item.added_at;
    }
    return row;
}

export function WaiterOrderEditor({ open, order, tables = [], activeOrders = [], initialTableId = '', onClose, onSaved }) {
    const [tableId, setTableId] = useState('');
    const [categories, setCategories] = useState([]);
    const [products, setProducts] = useState([]);
    const [activeCat, setActiveCat] = useState(null);
    const [cart, setCart] = useState([]);
    const [notes, setNotes] = useState('');
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');

    const tableOrder = useMemo(
        () => activeOrders.find((o) => o.table_id === tableId && !['PAID', 'CANCELLED'].includes(o.status)),
        [activeOrders, tableId],
    );
    const targetOrder = order?.id ? order : tableOrder;
    const isEdit = Boolean(targetOrder?.id);

    useEffect(() => {
        if (!open) return;
        setError('');
        const nextTableId = order?.table_id || initialTableId || tables[0]?.id || '';
        setTableId(nextTableId);
        setNotes(order?.notes || '');
        setCart((order?.items || []).map((it) => normalizeCartItem(it)));
        Promise.all([api.get('/r/categories'), api.get('/r/products')]).then(([cRes, pRes]) => {
            const cats = cRes.data.items || [];
            const prods = pRes.data.items || [];
            setCategories(cats);
            setProducts(prods.filter((p) => p.is_active !== false));
            setActiveCat(cats[0]?.id || null);
        }).catch((e) => setError(formatApiError(e)));
    }, [open, order, initialTableId, tables]);

    useEffect(() => {
        if (!open || order?.id) return;
        if (!tableOrder) return;
        setCart((tableOrder.items || []).map((it) => normalizeCartItem(it)));
        setNotes(tableOrder.notes || '');
    }, [open, order, tableOrder]);

    const filtered = useMemo(
        () => products.filter((p) => !activeCat || p.category_id === activeCat),
        [products, activeCat],
    );

    const total = useMemo(
        () => cart.reduce((sum, item) => sum + item.price * item.quantity, 0),
        [cart],
    );

    const addItem = (product, variant) => {
        if (!variant?.id) return;
        setCart((prev) => {
            const key = cartKey({ product_id: product.id, variant_id: variant.id });
            const idx = prev.findIndex((i) => cartKey(i) === key);
            if (idx >= 0) {
                const copy = [...prev];
                copy[idx] = { ...copy[idx], quantity: copy[idx].quantity + 1 };
                return copy;
            }
            return [...prev, normalizeCartItem({
                product_id: product.id,
                variant_id: variant.id,
                product_name: product.name,
                variant_name: variant.name,
                price: variant.price,
                quantity: 1,
            })];
        });
    };

    const changeQty = (key, delta) => {
        setCart((prev) => prev
            .map((item) => (cartKey(item) === key ? { ...item, quantity: item.quantity + delta } : item))
            .filter((item) => item.quantity > 0));
    };

    const removeItem = (key) => {
        setCart((prev) => prev.filter((item) => cartKey(item) !== key));
    };

    const handleSave = async () => {
        if (!tableId) {
            setError('Alege masa.');
            return;
        }
        setSaving(true);
        setError('');
        try {
            const payload = {
                items: cart.map(normalizeCartItem),
                notes,
            };
            if (isEdit) {
                await api.put(`/r/orders/${targetOrder.id}/items`, payload);
            } else {
                if (!cart.length) {
                    setError('Adaugă cel puțin un produs.');
                    setSaving(false);
                    return;
                }
                await api.post('/r/orders', { table_id: tableId, ...payload });
            }
            onSaved && onSaved();
            onClose && onClose();
        } catch (e) {
            setError(formatApiError(e));
        } finally {
            setSaving(false);
        }
    };

    const tableLabel = targetOrder?.table_number
        || tables.find((t) => t.id === tableId)?.number
        || '';

    return (
        <Modal
            open={open}
            title={isEdit ? `Comanda mesei ${tableLabel}` : `Comandă nouă — Masa ${tableLabel}`}
            onClose={onClose}
            maxWidth={520}
        >
            {isEdit && (
                <p style={{ color: 'var(--text-muted)', fontSize: '0.85rem', marginTop: 0, marginBottom: '0.9rem', lineHeight: 1.45 }}>
                    Adaugă produse din meniu, modifică cantitățile sau șterge linii — inclusiv ce a comandat clientul de pe QR.
                </p>
            )}

            <div style={{ marginBottom: '0.9rem' }}>
                <label className="label">Masă</label>
                <select
                    className="input"
                    value={tableId}
                    disabled={Boolean(order?.id)}
                    onChange={(e) => setTableId(e.target.value)}
                    data-testid="waiter-order-table"
                >
                    {sortTablesByNumber(tables).map((t) => (
                        <option key={t.id} value={t.id}>Masa {t.number} · {t.zone}</option>
                    ))}
                </select>
                {!order?.id && tableOrder && (
                    <div style={{ fontSize: '0.78rem', color: 'var(--amber)', marginTop: 4 }}>
                        Masa are comandă activă — modificările se aplică comenzii existente.
                    </div>
                )}
            </div>

            <div style={{ marginBottom: '0.5rem' }}>
                <label className="label">Meniu — adaugă produse</label>
            </div>
            <div style={{ display: 'flex', gap: 6, overflowX: 'auto', marginBottom: '0.8rem', paddingBottom: 4 }}>
                {categories.map((c) => (
                    <button
                        key={c.id}
                        type="button"
                        className={`btn btn-sm ${activeCat === c.id ? 'btn-primary' : 'btn-secondary'}`}
                        onClick={() => setActiveCat(c.id)}
                    >
                        {displayCategoryEmoji(c)} {c.name}
                    </button>
                ))}
            </div>

            <div style={{ maxHeight: 200, overflowY: 'auto', marginBottom: '0.9rem', border: '1px solid var(--border)', borderRadius: 10, padding: 8 }}>
                {filtered.map((p) => (
                    <div key={p.id} style={{ marginBottom: 8 }}>
                        <div style={{ fontWeight: 600, fontSize: '0.92rem', marginBottom: 4 }}>{p.name}</div>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                            {(p.variants || []).map((v) => (
                                <button
                                    key={v.id}
                                    type="button"
                                    className="btn btn-secondary btn-sm"
                                    style={{ justifyContent: 'space-between' }}
                                    onClick={() => addItem(p, v)}
                                    data-testid={`waiter-add-${p.id}-${v.id}`}
                                >
                                    <span>{v.name || 'Porție'}</span>
                                    <span>{Number(v.price).toFixed(2)} RON</span>
                                </button>
                            ))}
                        </div>
                    </div>
                ))}
                {!filtered.length && <div style={{ color: 'var(--text-muted)', fontSize: '0.85rem' }}>Niciun produs în categorie.</div>}
            </div>

            <div style={{ marginBottom: '0.9rem' }}>
                <label className="label">Produse în comandă ({cart.length})</label>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                    {cart.map((item) => {
                        const key = cartKey(item);
                        const isExtra = item.is_extra && item.source === 'client';
                        return (
                            <div
                                key={key}
                                className={isExtra ? 'order-item-extra' : 'order-item-regular'}
                                style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, fontSize: '0.88rem', padding: isExtra ? undefined : '6px 0', borderBottom: isExtra ? undefined : '1px solid var(--border)' }}
                            >
                                <span style={{ flex: 1 }}>
                                    {isExtra && <span className="order-items-extra-label" style={{ display: 'block', marginBottom: 2 }}>Extra client</span>}
                                    {item.quantity}× {item.product_name} · {item.variant_name}
                                </span>
                                <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                                    <button type="button" className="btn btn-ghost btn-sm" onClick={() => changeQty(key, -1)} aria-label="Scade"><Minus size={14} /></button>
                                    <span style={{ minWidth: 52, textAlign: 'right' }}>{(item.price * item.quantity).toFixed(2)}</span>
                                    <button type="button" className="btn btn-ghost btn-sm" onClick={() => changeQty(key, 1)} aria-label="Crește"><Plus size={14} /></button>
                                    <button type="button" className="btn btn-ghost btn-sm" onClick={() => removeItem(key)} aria-label="Șterge" data-testid={`waiter-remove-${key}`}><Trash2 size={14} /></button>
                                </div>
                            </div>
                        );
                    })}
                    {!cart.length && (
                        <div style={{ color: 'var(--text-muted)', fontSize: '0.85rem' }}>
                            {isEdit ? 'Comanda e goală — la salvare se anulează.' : 'Niciun produs adăugat.'}
                        </div>
                    )}
                </div>
            </div>

            <div style={{ marginBottom: '0.9rem' }}>
                <label className="label">Note comandă (opțional)</label>
                <input className="input" value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Ex: fără ceapă, alergie" />
            </div>

            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.9rem' }}>
                <span className="kicker">Total</span>
                <strong style={{ color: 'var(--amber)', fontSize: '1.1rem' }}>{total.toFixed(2)} RON</strong>
            </div>

            {error && <div style={{ color: 'var(--neon-magenta)', fontSize: '0.85rem', marginBottom: '0.8rem' }}>{error}</div>}

            <button
                type="button"
                className="btn btn-primary"
                style={{ width: '100%' }}
                disabled={saving || !tableId || (!isEdit && !cart.length)}
                onClick={handleSave}
                data-testid="waiter-order-save"
            >
                {saving ? 'Se salvează...' : (isEdit ? 'Salvează comanda' : 'Creează comanda')}
            </button>
        </Modal>
    );
}
